import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import {
  IconX,
  IconMapPin,
  IconClock,
  IconUser,
  IconPhone,
  IconEdit,
  IconPlus,
  IconArrowLeft,
  IconTrash,
  IconLocation,
} from "@tabler/icons-react";
import toast from "react-hot-toast";

import {
  usePickupLocationClientAddressData,
  usePickupLocationCreateMutation,
  useUpdateLocationMutation,
  useDeleteLocationMutation,
  useSetDefaultLocationMutation,
} from "../../../apis/pickupApis";
import CreateLocation from "../../ManifestManagement/_components/CreateLocation";
import { PickupFormData } from "../../../types/types";
import MessageModal from "../../../components/MessageModal";
import { usePermission } from "../../../hooks/usePermissionTabs";

interface Props {
  open: boolean;
  onClose: () => void;
  merchant: any;
}

const SectionHeader = ({ icon: Icon, title }: { icon: any; title: string }) => (
  <div className="flex items-center gap-2 border-b border-[#FBD037]/30 pb-2 mb-3">
    <div className="p-1.5 rounded-lg bg-[#FBD037]/20">
      <Icon size={16} className="text-[#212121]" stroke={1.5} />
    </div>
    <p className="text-[#212121]/80 uppercase text-[11px] font-bold tracking-wide">
      {title}
    </p>
  </div>
);

const ManagePickupLocationsModal: React.FC<Props> = ({
  open,
  onClose,
  merchant,
}) => {
  const { allowed: canCreatePickupLocation } = usePermission("pickup_locations", "create");
  const { allowed: canUpdatePickupLocation } = usePermission("pickup_locations", "update");
  const { allowed: canDeletePickupLocation } = usePermission("pickup_locations", "delete");

  const [view, setView] = useState<"list" | "form">("list");
  const [mode, setMode] = useState<"add" | "edit">("add");
  const [selectedLocation, setSelectedLocation] = useState<any>(null);
  const [locationToDelete, setLocationToDelete] = useState<string | null>(null);

  // Queries
  const {
    data: pickupLocations = [],
    refetch,
    isLoading: listLoading,
  } = usePickupLocationClientAddressData(merchant?.id);

  const { mutate: createLocation, isPending: createPending } =
    usePickupLocationCreateMutation();
  const { mutate: updateLocation, isPending: updatePending } =
    useUpdateLocationMutation();
  const { mutate: deleteLocation, isPending: deletePending } =
    useDeleteLocationMutation();
  const { mutate: setDefaultLocation, isPending: setDefaultPending } =
    useSetDefaultLocationMutation();

  // Form Setup
  const {
    register,
    handleSubmit,
    setValue,
    reset,
    watch,
    clearErrors,
    control,
    formState: { errors },
  } = useForm<PickupFormData>({
    defaultValues: {
      forClientId: merchant?.id || "",
      locationName: "",
      countryId: "",
      cityId: "",
      districtId: "",
      fullAddress: "",
      shortAddress: "",
      pinCode: "",
      latitude: "",
      longitude: "",
    },
  });

  // Handle location card selection for editing
  const handleLocationSelect = (location: any) => {
    if (!canUpdatePickupLocation) return;
    setMode("edit");
    setSelectedLocation(location);
    setView("form");
    reset({
      forClientId: location.forClientId || merchant.id,
      locationName: location.locationName || "",
      countryId: location.countryId || "",
      cityId: location.cityId || "",
      districtId: location.districtId || "",
      fullAddress: location.fullAddress || "",
      shortAddress: location.shortAddress || "",
      pinCode: location.pinCode || "",
      latitude: location.latitude || "",
      longitude: location.longitude || "",
    });
  };

  const handleOpenAddForm = () => {
    setMode("add");
    setSelectedLocation(null);
    setView("form");
    reset({
      forClientId: merchant.id,
      locationName: "",
      countryId: "",
      cityId: "",
      districtId: "",
      fullAddress: "",
      shortAddress: "",
      pinCode: "",
      latitude: "",
      longitude: "",
    });
  };

  const handleBackToList = () => {
    setView("list");
    setSelectedLocation(null);
  };

  const handleDeleteLocation = (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    if (!canDeletePickupLocation) {
      toast.error("You do not have permission to delete pickup locations");
      return;
    }
    setLocationToDelete(id);
  };

  const handleSetDefault = (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    if (!canUpdatePickupLocation) {
      toast.error("You do not have permission to update pickup locations");
      return;
    }
    setDefaultLocation(id, {
      onSuccess: () => {
        toast.success("Default pickup location updated successfully");
        refetch();
      },
      onError: (err: any) => {
        toast.error(err.message || "Failed to set default location");
      },
    });
  };

  const onSubmit = (data: PickupFormData) => {
    const payload = {
      ...data,
      forClientId: merchant.id,
    };

    if (mode === "edit" && selectedLocation?.id) {
      updateLocation(
        { id: selectedLocation.id, ...payload },
        {
          onSuccess: () => {
            toast.success("Pickup location updated successfully");
            refetch();
            handleBackToList();
          },
          onError: (err: any) => {
            toast.error(err.message || "Failed to update pickup location");
          },
        },
      );
    } else {
      createLocation(payload, {
        onSuccess: () => {
          toast.success("Pickup location created successfully");
          refetch();
          handleBackToList();
        },
        onError: (err: any) => {
          toast.error(err.message || "Failed to create pickup location");
        },
      });
    }
  };

  if (!open) return null;

  const displayLocations = Array.isArray(pickupLocations)
    ? pickupLocations
    : (pickupLocations as any)?.locations ||
      (pickupLocations as any)?.addresses ||
      (pickupLocations as any)?.data ||
      [];

  const modalContent = (
    <div className="relative bg-gradient-to-tr from-[#FDF8E5] to-[#FAF8EC] rounded-3xl shadow-sm border border-[#D2D2D2]/70 flex flex-col overflow-hidden h-[calc(100vh-7rem)]">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-white/50 shrink-0">
        <div className="flex items-center gap-3">
          {view === "form" && (
            <button
              type="button"
              onClick={handleBackToList}
              className="p-1.5 rounded-lg border border-slate-200 hover:bg-slate-100 transition text-slate-600 cursor-pointer"
              title="Back to list"
            >
              <IconArrowLeft size={16} />
            </button>
          )}
          <div>
            <h2 className="text-lg font-semibold text-[#212121]">
              {view === "list"
                ? "Pickup Locations"
                : mode === "add"
                  ? "Create Location"
                  : "Edit Location"}
            </h2>
            <p className="text-xs text-[#212121]/70 font-medium">
              {merchant?.name}{" "}
              <span className="text-[#FBD037] font-semibold">
                ({merchant?.clientId})
              </span>
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {view === "list" ? (
            <>
              {canCreatePickupLocation && (
                <button
                  type="button"
                  onClick={handleOpenAddForm}
                  className="px-4 py-2 flex items-center gap-2 bg-[#FBD037] text-[#212121] rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  title="Add Location"
                >
                  <IconPlus size={14} />
                  <span>Add</span>
                </button>
              )}
              <button
                type="button"
                onClick={onClose}
                className="p-2 rounded-lg border border-[#D2D2D2] hover:bg-gray-100 transition-colors text-slate-500 cursor-pointer"
              >
                <IconX size={18} />
              </button>
            </>
          ) : (
            <>
              <button
                type="submit"
                disabled={createPending || updatePending}
                className="px-4 py-2 bg-[#FBD037] text-[#212121] rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {mode === "add" ? "Create" : "Update"}
              </button>
              <button
                type="button"
                onClick={onClose}
                className="p-2 rounded-lg border border-[#D2D2D2] hover:bg-gray-100 transition-colors text-slate-500 cursor-pointer"
              >
                <IconX size={18} />
              </button>
            </>
          )}
        </div>
      </div>

      {/* Body */}
      {view === "list" ? (
        <div className="flex-1 overflow-y-auto p-5 scrollbar-thin">
          <div className="space-y-3 bg-white/40 rounded-2xl p-4 shadow-sm">
            <SectionHeader icon={IconLocation} title="Existing Pickups" />

            {listLoading ? (
              <div className="flex flex-col items-center justify-center py-12 space-y-2">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#FBD037]" />
                <p className="text-sm text-slate-500">
                  Loading pickup sites...
                </p>
              </div>
            ) : displayLocations.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 px-4 border border-dashed border-slate-200 rounded-2xl bg-white text-center">
                <IconMapPin size={32} className="text-slate-300 mb-2" />
                <p className="text-sm font-semibold text-slate-600">
                  No pickup sites found
                </p>
                <p className="text-xs text-slate-400 mt-1">
                  Add a location above to get started
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {displayLocations.map((loc: any) => (
                  <div
                    key={loc.id}
                    onClick={() => handleLocationSelect(loc)}
                    className={`group rounded-2xl border border-slate-100 p-4 transition-all duration-200 bg-white ${
                      canUpdatePickupLocation
                        ? "cursor-pointer hover:border-[#FBD037] hover:shadow-sm"
                        : "cursor-default"
                    }`}
                  >
                    <div className="flex justify-between items-start mb-2">
                      <h4 className="font-semibold text-slate-800 text-sm group-hover:text-[#FBD037] transition line-clamp-1">
                        {loc.locationName}
                      </h4>
                      <div className="flex items-center gap-1.5 shrink-0">
                        {loc.isDefault ? (
                          <span className="text-[9px] font-bold uppercase px-1.5 py-0.5 rounded bg-yellow-100 text-yellow-700">
                            Default
                          </span>
                        ) : (
                          canUpdatePickupLocation && (
                            <button
                              type="button"
                              onClick={(e) => handleSetDefault(e, loc.id)}
                              disabled={setDefaultPending}
                              className="text-[9px] font-bold uppercase px-1.5 py-0.5 rounded bg-slate-100 hover:bg-yellow-100 text-slate-500 hover:text-yellow-700 transition cursor-pointer"
                              title="Set as Default"
                            >
                              Set Default
                            </button>
                          )
                        )}
                        <span
                          className={`px-2 py-0.5 rounded-full text-[10px] font-medium capitalize ${
                            loc.status === "ACTIVE"
                              ? "bg-green-500/15 text-green-600"
                              : "bg-red-500/15 text-red-600"
                          }`}
                        >
                          {loc.status?.toLowerCase() || "active"}
                        </span>
                        {canDeletePickupLocation && (
                          <button
                            type="button"
                            onClick={(e) => handleDeleteLocation(e, loc.id)}
                            disabled={deletePending}
                            className="p-1 rounded text-red-500 hover:bg-red-50 hover:text-red-700 transition cursor-pointer"
                            title="Delete Location"
                          >
                            <IconTrash size={16} />
                          </button>
                        )}
                      </div>
                    </div>

                    <p className="text-xs text-slate-600 mb-3 flex items-start gap-1">
                      <IconMapPin
                        size={14}
                        className="text-slate-400 shrink-0 mt-0.5"
                      />
                      <span className="line-clamp-2">{loc.fullAddress}</span>
                    </p>

                    <div className="flex flex-col gap-1.5 pt-2 border-t border-slate-50 text-[11px] text-slate-500">
                      <div className="flex items-center gap-1">
                        <span className="font-semibold text-slate-400">
                          Area:
                        </span>
                        <span>
                          {loc.city ? `${loc.city}, ` : ""}
                          {loc.country || ""}
                        </span>
                      </div>
                      {loc.district && (
                        <div className="flex items-center gap-1">
                          <span className="font-semibold text-slate-400">
                            District:
                          </span>
                          <span>{loc.district}</span>
                        </div>
                      )}
                      {loc.pinCode && (
                        <div className="flex items-center gap-1">
                          <span className="font-semibold text-slate-400">
                            Postal Code:
                          </span>
                          <span>{loc.pinCode}</span>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="flex-1 overflow-y-auto">
          <CreateLocation
            register={register}
            control={control}
            errors={errors}
            setValue={setValue}
            watch={watch}
            reset={reset}
            clearErrors={clearErrors}
            mode={mode}
            defaultValues={selectedLocation}
            heightClass="h-auto"
            hideMerchantSelect={true}
          />
        </div>
      )}

      <MessageModal
        open={!!locationToDelete}
        onClose={() => setLocationToDelete(null)}
        title="Confirmation Required"
        subtitle="Are you sure you want to delete this pickup location?"
        img={"/img/inactive.svg"}
        showFooter={true}
        closeButtonText="Cancel"
        actionButtonText="Confirm"
        actionButtonOnClick={() => {
          if (locationToDelete) {
            deleteLocation(locationToDelete, {
              onSuccess: () => {
                toast.success("Pickup location deleted successfully");
                refetch();
                setLocationToDelete(null);
              },
              onError: (err: any) => {
                toast.error(err.message || "Failed to delete pickup location");
                setLocationToDelete(null);
              },
            });
          }
        }}
        isPending={deletePending}
        size="md"
      />
    </div>
  );

  return view === "form" ? (
    <form onSubmit={handleSubmit(onSubmit)} className="h-full">
      {modalContent}
    </form>
  ) : (
    modalContent
  );
};

export default ManagePickupLocationsModal;
